import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'How exactly do non-dividend stocks save on taxes?',
    a: 'Two structural advantages baked into U.S. tax code. First, tax deferral: when a company retains earnings instead of paying them out, you owe zero tax on those earnings until you sell the stock — sometimes decades later. With dividends, you owe tax every single year even if you reinvest them through DRIP. Second, capital-gains rates apply on the eventual sale. Long-term capital gains (positions held more than one year) are taxed at preferential federal rates of 0%, 15%, or 20% depending on your income tier — generally lower than ordinary-income rates for high earners.',
  },
  {
    q: 'Don\u2019t qualified dividends get the same low rates?',
    a: 'Yes — qualified dividends ARE taxed at the same 0% / 15% / 20% federal rates as long-term capital gains. The difference is timing, not rate. With qualified dividends, you owe the 15% (or 20%) tax EVERY YEAR even if you reinvest. With capital appreciation, you owe the same rate but ONLY in the year you sell. Across a 30-year compounding curve, that timing difference is worth tens or hundreds of thousands of dollars depending on portfolio size. The example math is below.',
  },
  {
    q: 'What about the Net Investment Income Tax (NIIT)?',
    a: 'NIIT is a 3.8% federal surtax that applies to investment income (including dividends AND realized capital gains) for higher earners above modified AGI thresholds set in the Affordable Care Act. The thresholds are $200,000 for single filers and $250,000 for married-filing-jointly (unchanged since enactment — Congress has not indexed them for inflation). Non-dividend stocks defer NIIT exposure the same way they defer capital-gains tax — you only owe it in the year you sell. Always check the current IRS rules for your filing year.',
  },
  {
    q: 'What\u2019s the step-up in basis loophole?',
    a: 'When you die holding appreciated assets, U.S. tax code (Internal Revenue Code Section 1014) resets the cost basis of those assets to their fair market value at the date of your death. Your heirs inherit at the new "stepped-up" basis, which means all the embedded capital gains accumulated during your lifetime are wiped out for tax purposes. This is the single most powerful tax provision in the U.S. code for long-term equity holders. A Berkshire Hathaway shareholder who bought in 1965 and held until death in 2024 would have generated roughly $60 million of unrealized gains on a $1,000 starting investment — and their heirs would inherit those shares with zero embedded capital-gains tax owed. Dividends, which are taxed in the year received, never qualify for this treatment.',
  },
  {
    q: 'Are there any tax DOWNSIDES to non-dividend investing?',
    a: 'Two worth knowing. First, you can\u2019t use a long-term capital loss to offset more than $3,000 of ordinary income per year (excess losses carry forward indefinitely, but you can\u2019t accelerate them). Second, the wash-sale rule (IRC Section 1091) prevents you from claiming a loss if you buy the same security within 30 days before or after the sale — so tax-loss harvesting requires more discipline with concentrated growth positions. Neither of these is a dealbreaker, but they require a position-tracking discipline that dividend investors don\u2019t need.',
  },
  {
    q: 'Where do non-dividend stocks belong — taxable account or IRA?',
    a: 'Both work, but the taxable-account placement is structurally more powerful FOR non-dividend stocks. Inside a traditional IRA or 401(k), withdrawals are taxed as ordinary income — so the preferential long-term capital gains rate is wasted. Inside a Roth IRA, growth is tax-free forever, which is great but loses the step-up-in-basis advantage. The classic asset-location playbook: keep high-yield REITs and ordinary-income dividend payers INSIDE tax-advantaged accounts (so the annual dividend tax friction is sheltered), and run your high-conviction non-dividend compounders in your TAXABLE brokerage account (so you keep the tax deferral and the step-up at death). This isn\u2019t tax advice — confirm with a CPA before restructuring large positions.',
  },
];

export default function LearnTaxEfficiencyNonDividend() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Tax Efficiency Secrets — How Capital Gains Beat Dividend Income Taxes | TwylightCrow"
      description="The structural tax advantages of non-dividend stocks: full deferral until sale, preferential long-term capital-gains rates, step-up in basis at death, and the asset-location playbook for combining dividend income with non-dividend compounders."
      canonical="https://twylightcrow.com/learn/tax-efficiency-non-dividend"
      guidePath="/learn/tax-efficiency-non-dividend"
      sectionLabel="Capital Allocation &amp; Tax"
      sectionAnchor="non-div-capital-allocation"
      kicker="Learn · Non-Dividend Stocks"
      h1="Tax Efficiency Secrets — How Capital Gains Beat Dividend Income Taxes"
      datePublished="2026-02-28"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/capital-appreciation-playbook', label: 'The Capital Appreciation Playbook — the mindset that makes the tax math work' },
        { to: '/learn/anatomy-of-a-stock-buyback', label: 'The Anatomy of a Stock Buyback — buybacks as a tax-deferred dividend' },
        { to: '/learn/niit-surtax', label: 'NIIT Surtax — the 3.8% surcharge above income thresholds' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs Ordinary Dividends — when the lower rates actually apply' },
      ]}
    >
      <p>
        Dividends are real money in your account, but they come with an
        invisible recurring cost: <strong>annual tax friction</strong>.
        Every qualified dividend triggers federal capital-gains-rate tax in
        the year received. Every ordinary dividend triggers ordinary-income
        tax. Whether you spend the cash or DRIP it back in, the IRS takes
        its slice first. Over a 30-year compounding window, that drag
        quietly costs the average dividend investor a six-figure sum
        compared to a comparable non-dividend portfolio. This guide is the
        math.
      </p>
      <p>
        None of what follows is tax advice. Always confirm with a CPA
        before restructuring large positions. But the structural reasons
        non-dividend stocks beat dividend payers on after-tax compounding
        are baked into U.S. tax code, and worth understanding before you
        let &ldquo;dividend safety&rdquo; lock you out of a higher-return
        portfolio.
      </p>

      <h2>The two structural tax advantages</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Advantage</th>
            <th>How it works</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">1. Tax deferral</td>
            <td>When a company retains earnings instead of paying them out, you owe ZERO federal tax on those retained earnings until you sell the stock. The entire compounding curve runs untaxed for as many years as you choose to hold.</td>
          </tr>
          <tr>
            <td className="font-semibold">2. Preferential rates (long-term)</td>
            <td>Positions held more than one year qualify for long-term capital-gains rates of 0%, 15%, or 20% depending on your income tier. Identical to the qualified-dividend rate, but applied ONCE at sale rather than annually.</td>
          </tr>
        </tbody>
      </table>
      <p>
        Both effects compound. The deferral lets you invest 100% of every
        retained dollar for decades. The preferential rate lets you keep
        80–85% of the eventual gain (or 100% if you die holding —
        more on that in a moment).
      </p>

      <h2>The math: $100,000 over 30 years</h2>
      <p>
        Side-by-side comparison of two portfolios, each starting at
        $100,000 and earning a 9% total annual return for 30 years. Both
        investors are in the 15% qualified-dividend / 15% long-term
        capital-gains federal bracket. State taxes are excluded for
        simplicity (they&rsquo;d make the gap wider, not narrower).
        Portfolio B&rsquo;s basis grows each year as DRIP&rsquo;d after-tax
        dividends are reinvested.
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Portfolio</th>
            <th>Annual mix</th>
            <th>Pre-sale value</th>
            <th>Basis at year 30</th>
            <th>LTCG owed at sale (15%)</th>
            <th>Net after-tax wealth</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">A — Non-dividend compounder</td>
            <td>9% capital appreciation, 0% dividend</td>
            <td className="font-mono">$1,326,768</td>
            <td className="font-mono">$100,000</td>
            <td className="font-mono">$184,015</td>
            <td className="font-mono text-emerald-600">$1,142,753</td>
          </tr>
          <tr>
            <td className="font-semibold">B — Dividend payer (DRIP)</td>
            <td>5% capital appreciation + 4% qualified dividend (15% tax each year, reinvested)</td>
            <td className="font-mono">$1,178,407</td>
            <td className="font-mono">$549,231</td>
            <td className="font-mono">$94,376</td>
            <td className="font-mono">$1,084,030</td>
          </tr>
        </tbody>
      </table>
      <p>
        The non-dividend portfolio ends roughly <strong>$58,700 richer
        in after-tax wealth</strong> on the same $100,000 starting
        investment and the same 9% gross annual return. The pre-sale
        wealth gap is even larger — about $148,000 — because Portfolio
        B&rsquo;s annual tax payments compound into ground that can never
        be recovered. Scale either gap up to a $1 million starting
        balance and you&rsquo;re looking at roughly $587,000 of after-tax
        difference, or $1.48 million pre-sale.
      </p>
      <p>
        And this comparison treats both portfolios identically at sale.
        The non-dividend portfolio has one more lever the dividend
        portfolio doesn&rsquo;t — the step-up at death — which can wipe
        out that entire $1,226,768 unrealized capital gain for the heirs.
        Read on.
      </p>

      <h2>The death loophole — Section 1014 step-up in basis</h2>
      <p>
        This is the single most powerful tax provision in U.S. code for
        long-term equity holders, and it applies almost exclusively to
        non-dividend (or buyback-heavy) compounders. Internal Revenue
        Code Section 1014 resets the cost basis of inherited assets to
        their fair market value at the date of the original owner&rsquo;s
        death. All the embedded capital gains accumulated during the
        original owner&rsquo;s lifetime are <strong>wiped out for tax
        purposes</strong>. Heirs inherit at the new basis and could
        immediately sell with zero capital-gains tax owed.
      </p>
      <p>
        A Berkshire Hathaway shareholder who bought $1,000 of stock in
        1965 and held until death in 2024 generated roughly $60 million
        of unrealized capital gains during their lifetime. Under Section
        1014, those gains were never taxed. The heirs inherited the
        shares with a fresh basis at the 2024 market value. Dividends, by
        contrast, are taxed in the year received — they never qualify
        for this treatment.
      </p>
      <p>
        This is why a meaningful share of generational family wealth in
        the U.S. consists of long-held non-dividend equity positions.
        Apple shareholders who bought during the no-dividend era
        (1995–2012) and have held through to today have nearly two
        decades of fully-deferred appreciation accumulating toward the
        same outcome.
      </p>

      <h2>Tax-loss harvesting is a separate tool — not a non-dividend exclusive</h2>
      <p>
        You&rsquo;ll see other writeups claim non-dividend stocks unlock
        special &ldquo;tax-loss harvesting&rdquo; strategies. That&rsquo;s
        misleading. Tax-loss harvesting (selling a losing position to
        realize the loss against your gains) applies to EVERY equity
        security — dividend payers and non-payers alike. The only
        non-dividend nuance is that with concentrated growth positions
        you tend to have larger position sizes per holding, which makes
        the wash-sale rule (IRC Section 1091, 30-day before-or-after
        window) more constraining. Plan for a 30-day cooling-off period
        between selling a loss and buying back substantially identical
        securities.
      </p>

      <h2>Asset location: where each stock type belongs</h2>
      <p>
        If you hold BOTH dividend and non-dividend stocks (most balanced
        portfolios do), the structurally correct location for each is
        the opposite of what most retail investors do.
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Holding</th>
            <th>Best home</th>
            <th>Why</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">High-yield REITs</td>
            <td>Roth IRA or traditional IRA</td>
            <td>REIT distributions are mostly ordinary income (no qualified-dividend treatment). Sheltering them eliminates the worst annual tax friction.</td>
          </tr>
          <tr>
            <td className="font-semibold">Ordinary-income dividend payers (BDCs, MLPs)</td>
            <td>Roth IRA</td>
            <td>Same logic — high-tax distributions, full shelter inside the Roth. MLPs have UBTI complications outside the Roth.</td>
          </tr>
          <tr>
            <td className="font-semibold">Qualified-dividend Aristocrats (KO, PG, JNJ)</td>
            <td>Either taxable or Roth — context-dependent</td>
            <td>15% qualified-dividend friction in taxable is tolerable for moderate yields. Roth shelters it entirely. Either works.</td>
          </tr>
          <tr>
            <td className="font-semibold">Non-dividend compounders (BRK.B, AMZN, GOOG)</td>
            <td className="font-semibold text-emerald-600">Taxable brokerage</td>
            <td>You want the step-up-in-basis at death AND the long-term capital-gains rate. Both are lost inside a traditional IRA (withdrawals are ordinary income).</td>
          </tr>
        </tbody>
      </table>

      <h2>Three rules of thumb for tax-efficient non-dividend investing</h2>
      <ol>
        <li>
          <strong>Hold positions more than 12 months whenever possible.</strong>{' '}
          Short-term capital gains (positions held one year or less) are
          taxed at ordinary-income rates, which can be more than double
          the long-term rate for high earners. Time your sales.
        </li>
        <li>
          <strong>Hold your non-dividend compounders in your taxable brokerage account.</strong>{' '}
          Don&rsquo;t waste preferential capital-gains rates inside a
          traditional IRA where they convert back to ordinary income at
          withdrawal.
        </li>
        <li>
          <strong>Use the AI thesis &ldquo;What Would Change This View&rdquo;
          bullets as written sell triggers.</strong> A non-dividend exit
          discipline matters more than a dividend one, because each sale
          is a taxable event. Pre-define the conditions, copy them into
          your spreadsheet, and don&rsquo;t fire-sell on a 20% drawdown
          unless one of those conditions has flipped.
        </li>
      </ol>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }, i) => (
          <div key={i} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open a non-dividend ticker you already own (or are considering)
        on the <Link to="/dashboard">Dashboard</Link>. Click into the
        Stock Modal and select the Capital Analytics tab. Scroll to the
        AI Thesis section at the bottom — copy the three bullets under
        &ldquo;What Would Change This View&rdquo; into your portfolio
        tracker as written sell triggers. That gives you the same
        decision discipline a dividend cut would provide for a dividend
        stock: a measurable, pre-committed reason to exit.
      </p>

      <p className="mt-6">
        Always confirm your specific tax-bracket math with a CPA. The IRS
        publishes updated capital-gains thresholds annually — current
        rules and rates are documented in IRS Publication 550 and
        Publication 590-B.
      </p>
      <p className="text-xs text-muted-foreground italic">
        This guide is educational, not tax advice. Federal capital-gains
        brackets and the NIIT thresholds are adjusted by Congress and the
        IRS on different schedules — verify current numbers before making
        tax-driven trades.
      </p>
    </LearnPage>
  );
}
